import { Product } from '@airship-platform/airship-proposal';
import { LoadingButton } from '@airship-platform/jam-web/components';
import { useMemo } from 'react';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useItemGroupMutations } from '../../proposal-item-groups';

interface AddSubItemButtonProps {
  product: Product;
  itemGroupRootId: string;
}

const AddSubItemButton: React.FC<AddSubItemButtonProps> = ({
  product,
  itemGroupRootId,
}) => {
  const { currentOption } = useAppointmentProposalOptions();
  const { proposalProducts, isLoadingProductIds } =
    useAppointmentProposalOptionProducts();
  const { addSubItem, getChildren } = useItemGroupMutations();

  const root = useMemo(
    () =>
      proposalProducts.find(
        (p) => p.appointmentProposalOptionProductId === itemGroupRootId
      ),
    [proposalProducts, itemGroupRootId]
  );

  const isAlreadyAdded = useMemo(
    () =>
      getChildren(itemGroupRootId).some(
        (child) => child.pricebookId === product.pricebookId
      ) || root?.pricebookId === product.pricebookId,
    [getChildren, itemGroupRootId, product.pricebookId, root?.pricebookId]
  );

  if (!root) {
    return null;
  }

  const onClick = () => {
    const defaultPrice = product.prices.find(
      (p) => p.priceType === product.defaultPriceType
    );

    return addSubItem(root, {
      ...product,
      jobId: root.jobId,
      groupingName: product.type === 'MATERIAL' ? 'ADDONS' : 'SYSTEM',
      pricebookPrices: product.prices,
      currency: 'USD',
      quantity: 1,
      price: defaultPrice?.value || 0,
      taxable: defaultPrice?.taxable || false,
      linkedItems: product.linkedItems.filter(
        (item) =>
          item.priceType ===
          (product.defaultPriceType === 'DYNAMIC'
            ? 'STANDARD'
            : product.defaultPriceType)
      ),
    });
  };

  return (
    <LoadingButton
      color="softGold"
      loading={isLoadingProductIds.includes(product.pricebookId)}
      disabled={isAlreadyAdded || (currentOption?.isReadOnly ?? false)}
      onClick={onClick}
    >
      Add sub-item
    </LoadingButton>
  );
};

export default AddSubItemButton;
